import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  DataGridPremium,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridSortDirection,
  GridToolbarContainer,
} from "@mui/x-data-grid-premium";
import TableToolbar from "./TableToolbar";
import { OrderDirection, defaultParams } from "../../types/searchParams";
import SearchResults from "../../types/searchResults";

interface Props {
  columns: GridColDef[];
  rows: any[];
  allowExport?: boolean;
  page?: number;
  pageSize?: number;
  totalRows?: number;
  changePage?: (pageNumber?: number, pageSize?: number) => void;
  loading?: boolean;
  onCellClick?: (params: any) => void;
  getCellClassName?: (params: GridCellParams<any, any, any>) => string;
  handleSort?: (orderBy?: string, orderDirection?: OrderDirection) => void;
  handlePreExport?: () => Promise<any>;
  highlightId?: number;
  columnVisibility?: GridColumnVisibilityModel;
  handleColumnVisibilityChange?: (
    model: GridColumnVisibilityModel,
    details: GridCallbackDetails
  ) => void;
  filterModel?: GridFilterModel;
  handleFilterModelChange?: (
    model: GridFilterModel,
    details: GridCallbackDetails<"filter">
  ) => void;
  displayRows?: (array: SearchResults) => Record<string, string | undefined>[];
}

const DataGridTable: React.FC<Props> = (props) => {
  const {
    columns,
    rows,
    allowExport,
    page,
    pageSize,
    totalRows,
    changePage,
    loading,
    onCellClick,
    getCellClassName,
    handleSort,
    handlePreExport,
    highlightId,
    columnVisibility,
    handleColumnVisibilityChange,
    filterModel,
    handleFilterModelChange,
    displayRows,
  } = props;
  const theme = useTheme();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: (page ?? defaultParams.pageNumber ?? 1) - 1,
    pageSize: pageSize ?? defaultParams.pageSize ?? rows.length,
  });

  useEffect(() => {
    setPaginationModel({
      page: (page ?? defaultParams.pageNumber ?? 1) - 1,
      pageSize: pageSize ?? defaultParams.pageSize ?? rows.length,
    });
  }, [page, pageSize, rows]);

  const CustomExportToolbar = () => (
    <GridToolbarContainer
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
      }}
    >
      <TableToolbar
        totalRows={totalRows!}
        handlePreExport={handlePreExport}
        loading={loading}
        displayRows={displayRows}
      />
    </GridToolbarContainer>
  );

  const getOrderDirection = (direction: GridSortDirection) => {
    switch (direction) {
      case "asc":
        return "Ascending";
      case "desc":
        return "Descending";
      default:
        return undefined;
    }
  };

  const onPageChange = (model: GridPaginationModel) => {
    changePage?.(model.page + 1, model.pageSize);
    setPaginationModel({ page: model.page, pageSize: model.pageSize });
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        width: "100%",
        textAlign: "center",
        "& .odd": {
          backgroundColor: theme.palette.background.default,
        },
        "& .MuiDataGrid-menuIconButton": {
          color: "white",
        },
        "& .MuiDataGrid-sortIcon": {
          color: "white",
        },
        "& .MuiDataGrid-columnHeaderTitleContainer": {
          justifyContent: "center",
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: "bold",
        },
        "& .MuiDataGrid-container--top [role=row]": {
          backgroundColor: theme.palette.info.main,
          color: "white",
        },
      }}
    >
      {!loading && rows.length ? (
        <DataGridPremium
          // autosizeOnMount
          pagination
          getRowId={(row) => row.id}
          columns={columns}
          rows={rows}
          rowCount={changePage ? totalRows ?? rows.length : undefined}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[5, 10, 25]}
          rowHeight={40}
          paginationMode={changePage ? "server" : "client"}
          paginationModel={paginationModel}
          onPaginationModelChange={onPageChange}
          columnVisibilityModel={columnVisibility}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          slots={{
            toolbar: allowExport ? CustomExportToolbar : undefined,
          }}
          slotProps={{
            cell: {
              style: {
                display: "flex",
                alignItems: "center",
                textAlign: "center",
                paddingTop: "10px",
                paddingBottom: "10px",
              },
            },
          }}
          getRowHeight={() => "auto"}
          onCellClick={onCellClick}
          getCellClassName={getCellClassName}
          getRowClassName={(params) =>
            params.id === highlightId
              ? "highlight"
              : params.indexRelativeToCurrentPage % 2 === 0
              ? "even"
              : "odd"
          }
          sortingMode={handleSort ? "server" : "client"}
          onSortModelChange={(model) => {
            if (model.length > 0) {
              handleSort?.(model[0].field, getOrderDirection(model[0].sort));
            } else {
              handleSort?.(undefined, undefined);
            }
          }}
          filterMode={
            !!filterModel && !!handleFilterModelChange ? "server" : "client"
          }
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          disableColumnFilter
          sx={{
            flex: 1,
            "@media print": {
              ".MuiDataGrid-main": {
                width: "fit-content",
              },
            },
          }}
        />
      ) : loading ? (
        <Box
          sx={{
            height: 100,
            backgroundColor: theme.palette.background.default,
            color: "#fff",
            borderRadius: "5px",
            alignContent: "center",
          }}
        >
          <CircularProgress size="40px" />
          {/* <Typography>Loading table data...</Typography> */}
        </Box>
      ) : (
        <Box
          sx={{
            height: 100,
            backgroundColor: theme.palette.background.default,
            color: "#fff",
            borderRadius: "5px",
            alignContent: "center",
          }}
        >
          <Typography>No results to display</Typography>
        </Box>
      )}
    </Box>
  );
};

export default DataGridTable;
